import React, { useState } from 'react';
import styled from 'styled-components';

const EmployeeFilterContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 10;
`;

const EmployeeFilterButton = styled.button`
  background: ${({ $hasSelection }) => ($hasSelection ? 'var(--primary-green-light, #e6f4ea)' : 'white')};
  color: ${({ $hasSelection }) => ($hasSelection ? 'var(--primary-green, #2D882D)' : '#374151')};
  border: 1px solid ${({ $hasSelection }) => ($hasSelection ? 'var(--primary-green, #2D882D)' : '#e5e7eb')};
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
  will-change: transform, background-color;
  white-space: nowrap;
  box-shadow: ${({ $hasSelection }) => ($hasSelection ? '0 1px 2px rgba(0,0,0,0.05)' : 'none')};

  &:hover {
    background: ${({ $hasSelection }) => ($hasSelection ? 'var(--primary-green-light, #e6f4ea)' : '#f9fafb')};
    border-color: ${({ $hasSelection }) => ($hasSelection ? 'var(--primary-green, #2D882D)' : '#d1d5db')};
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 600px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.875rem;
  }
`;

const EmployeeDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  min-width: 200px;
  
  transform: translateZ(0);
  will-change: transform;
  
  @media (max-width: 600px) {
    right: 0;
    // left: auto;
    min-width: 180px;
    max-height:200px;
  }
`;

const EmployeeOption = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: transform 0.15s ease;
  font-size: 0.875rem;
  will-change: transform;
  
  &:hover {
    background-color: #f9fafb;
  }

  input[type="checkbox"] {
    margin-right: 0.5rem;
    accent-color: var(--primary-green, #2D882D);
  }
`;

const EmployeeChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const EmployeeChip = styled.div`
  background: var(--primary-green-light, #e6f4ea);
  color: var(--primary-green, #2D882D);
  border: 1px solid var(--primary-green, #2D882D);
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top:.5rem;
  
  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    margin-left: 0.2rem;
    font-size: 0.9rem;
    line-height: 1;
    
    &:hover {
      opacity: 0.7;
    }
  }
`;

const EmployeeFilterChip = ({ 
  uniqueEmployees = [], 
  selectedEmployees = [], 
  onEmployeeToggle, 
  onRemoveEmployee, 
  onClearAllEmployees 
}) => {
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);

  const handleEmployeeToggle = (employeeId) => {
    onEmployeeToggle(employeeId);
  };

  const handleRemoveEmployee = (employeeId) => {
    onRemoveEmployee(employeeId);
  };

  const clearAllEmployees = () => {
    onClearAllEmployees();
    setIsEmployeeDropdownOpen(false);
  };

  const selectedEmployeeNames = selectedEmployees
    .map(id => uniqueEmployees.find(emp => emp.id === id)?.name)
    .filter(Boolean);

  return (
    <>
      <EmployeeFilterContainer>
        <EmployeeFilterButton
          $hasSelection={selectedEmployees.length > 0}
          onClick={() => setIsEmployeeDropdownOpen(!isEmployeeDropdownOpen)}
        >
          👥 Employees {selectedEmployees.length > 0 && `(${selectedEmployees.length})`}
        </EmployeeFilterButton>
        
        {isEmployeeDropdownOpen && (
          <EmployeeDropdown>
            <EmployeeOption>
              <input
                type="checkbox"
                checked={selectedEmployees.length === 0}
                onChange={clearAllEmployees}
              />
              All Employees
            </EmployeeOption>
            {uniqueEmployees.map(employee => (
              <EmployeeOption key={employee.id}>
                <input
                  type="checkbox"
                  checked={selectedEmployees.includes(employee.id)}
                  onChange={() => handleEmployeeToggle(employee.id)}
                />
                {employee.name}
              </EmployeeOption>
            ))}
          </EmployeeDropdown>
        )}
      </EmployeeFilterContainer>

      {selectedEmployees.length > 0 && (
        <EmployeeChipsContainer>
          {selectedEmployeeNames.map((name, index) => (
            <EmployeeChip key={selectedEmployees[index]}>
              {name}
              <button aria-label={`Remove ${name}`} style={{ userSelect: 'none' }} onClick={() => handleRemoveEmployee(selectedEmployees[index])}>
                x
              </button>
            </EmployeeChip>
          ))}
        </EmployeeChipsContainer>
      )}
    </>
  );
};

export default EmployeeFilterChip ;